// Impressum (Offenlegung gemäß § 5 ECG, § 14 UGB, § 25 MedienG, § 24 MedienG).
// Public route — no auth required. Linked from public homepage footer.

import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { COMPANY } from '@4m/shared';
import { formatDate } from '@/lib/formatLocale';

export const Route = createFileRoute('/impressum')({
  component: ImpressumPage,
});

function ImpressumPage() {
  return (
    <main className="min-h-screen bg-paper text-ink">
      <header className="border-b border-line">
        <div className="mx-auto flex max-w-3xl items-center gap-4 px-6 py-5">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-medium uppercase text-ink-3 tracking-[0.1em] hover:text-ink"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Zur Startseite
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-6 py-12">
        <h1 className="font-display text-4xl tracking-tight text-ink">Impressum</h1>
        <p className="mt-2 text-sm text-ink-3">
          Offenlegung gemäß § 5 ECG, § 14 UGB, § 25 MedienG.
        </p>

        <section className="mt-10 space-y-1.5 text-sm leading-relaxed text-ink-2">
          <p className="font-display text-xl text-ink">{COMPANY.legalName}</p>
          <p>{COMPANY.address.formatted}</p>
          <p>
            Tel.:{' '}
            <a className="underline-offset-2 hover:underline" href={`tel:${COMPANY.contact.phoneE164}`}>
              {COMPANY.contact.phone}
            </a>{' '}
            · E-Mail:{' '}
            <a className="underline-offset-2 hover:underline" href={`mailto:${COMPANY.contact.email}`}>
              {COMPANY.contact.email}
            </a>
          </p>
          <p>
            Web:{' '}
            <a
              className="underline-offset-2 hover:underline"
              href={COMPANY.contact.website}
              target="_blank"
              rel="noopener noreferrer"
            >
              {COMPANY.contact.website.replace(/^https?:\/\//, '')}
            </a>
          </p>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-2xl text-ink">Firmen- und Steuerdaten</h2>
          <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-[180px_1fr]">
            <dt className="font-medium text-ink-3">Firmenbuchnummer</dt>
            <dd className="text-ink">FB Nr. {COMPANY.legal.firmenbuchNr}</dd>

            <dt className="font-medium text-ink-3">Firmenbuchgericht</dt>
            <dd className="text-ink">{COMPANY.legal.handelsgericht}</dd>

            <dt className="font-medium text-ink-3">Firmensitz</dt>
            <dd className="text-ink">{COMPANY.legal.firmensitz}</dd>

            <dt className="font-medium text-ink-3">Gerichtsstand</dt>
            <dd className="text-ink">{COMPANY.legal.gerichtsstand}</dd>

            <dt className="font-medium text-ink-3">Steuernummer</dt>
            <dd className="text-ink">{COMPANY.legal.steuerNr}</dd>

            <dt className="font-medium text-ink-3">UID-Nummer</dt>
            <dd className="text-ink">{COMPANY.legal.uid}</dd>

            <dt className="font-medium text-ink-3">Bankverbindung (IBAN)</dt>
            <dd className="font-mono text-ink">{COMPANY.banking.ibanFormatted}</dd>
          </dl>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-2xl text-ink">Unternehmensgegenstand</h2>
          <p className="mt-3 text-sm leading-relaxed text-ink-2">
            Immobilienmakler · Vermittlung des Kaufes, Verkaufes und der Anmietung von
            Liegenschaften und Wohnungen in Wien und Niederösterreich. Tätigkeit als
            Doppelmakler im Sinne des § 5 Maklergesetz.
          </p>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-2xl text-ink">Mitgliedschaften</h2>
          <p className="mt-3 text-sm leading-relaxed text-ink-2">
            Wirtschaftskammer Wien (WKO), Fachgruppe der Immobilien- und
            Vermögenstreuhänder. Es gelten die Allgemeinen Geschäftsbedingungen für
            Immobilien- und Vermögenstreuhänder in der jeweils gültigen Fassung.
          </p>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-2xl text-ink">Aufsichtsbehörde</h2>
          <p className="mt-3 text-sm leading-relaxed text-ink-2">
            Magistratisches Bezirksamt für den 5. Wiener Gemeindebezirk.
          </p>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-2xl text-ink">Online-Streitbeilegung</h2>
          <p className="mt-3 text-sm leading-relaxed text-ink-2">
            Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung
            (OS) bereit, die Sie unter{' '}
            <a
              className="underline-offset-2 hover:underline"
              href="https://ec.europa.eu/consumers/odr"
              target="_blank"
              rel="noopener noreferrer"
            >
              ec.europa.eu/consumers/odr
            </a>{' '}
            finden. Wir sind nicht verpflichtet, an Streitbeilegungsverfahren vor einer
            Verbraucherschlichtungsstelle teilzunehmen, sind aber dazu bereit.
          </p>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-2xl text-ink">Haftungsausschluss</h2>
          <p className="mt-3 text-sm leading-relaxed text-ink-2">
            Trotz sorgfältiger inhaltlicher Kontrolle übernehmen wir keine Haftung für
            die Inhalte externer Links. Für den Inhalt der verlinkten Seiten sind
            ausschließlich deren Betreiber verantwortlich.
          </p>
        </section>

        <p className="mt-12 text-xs text-ink-3">
          Stand: {formatDate(new Date(COMPANY.version))}
        </p>
      </article>

      <footer className="border-t border-line py-6">
        <div className="mx-auto max-w-3xl px-6 text-center text-xs text-ink-3">
          © {new Date().getFullYear()} {COMPANY.legalName}
        </div>
      </footer>
    </main>
  );
}
